import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import moment from 'moment'
import DescriptionIcon from '@mui/icons-material/Description'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import HistoryIcon from '@mui/icons-material/History'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { toast } from 'react-toastify'
import { getTaskReports, downloadTaskReport, getTaskReportVersions } from 'services/taskService'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import SearchableSelect from 'components/SearchableSelect'

const getUnitList = (task) => {
  if (!task) return []
  if (task.units?.length > 0) return task.units.map(u => ({ id: u.id, name: u.name }))
  if (task.unit?.id) return [{ id: task.unit.id, name: task.unit.name }]
  return []
}

const TaskReports = ({ buildingPassportId }) => {
  const [reports, setReports] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [meta, setMeta] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)
  const [unitFilter, setUnitFilter] = useState('')
  const [expandedVersions, setExpandedVersions] = useState({})
  const [versionData, setVersionData] = useState({})
  const [loadingVersions, setLoadingVersions] = useState({})

  const abortRef = React.useRef(null)

  const fetchReports = async (pageNum = 1) => {
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setIsLoading(true)
    setError(null)
    try {
      const response = await getTaskReports({
        buildingPassportId,
        page: pageNum,
        perPage: 15
      })
      if (!controller.signal.aborted) {
        setReports(response.data.data || [])
        setMeta(response.data.meta || null)
        setPage(pageNum)
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError('Failed to load task reports')
        console.error(err)
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (buildingPassportId) {
      fetchReports()
    }
    return () => {
      if (abortRef.current) abortRef.current.abort()
    }
  }, [buildingPassportId])

  const handleDownload = async (report) => {
    setDownloadingId(report.id)
    try {
      const response = await downloadTaskReport({
        buildingPassportId,
        reportId: report.id
      })
      const link = document.createElement('a')
      link.href = response.data.downloadUrl
      link.download = response.data.filename || report.filename || 'report.pdf'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error('Failed to download report:', err)
      toast.error('Failed to download report. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleToggleVersions = async (report) => {
    const taskId = report.task?.id
    if (!taskId) return

    if (expandedVersions[taskId]) {
      setExpandedVersions(prev => ({ ...prev, [taskId]: false }))
      return
    }

    setExpandedVersions(prev => ({ ...prev, [taskId]: true }))

    if (versionData[taskId]) return

    setLoadingVersions(prev => ({ ...prev, [taskId]: true }))
    try {
      const response = await getTaskReportVersions({
        buildingPassportId,
        taskId
      })
      setVersionData(prev => ({ ...prev, [taskId]: response.data.data || [] }))
    } catch (err) {
      console.error('Failed to load versions:', err)
      toast.error('Failed to load report versions.')
      setExpandedVersions(prev => ({ ...prev, [taskId]: false }))
    } finally {
      setLoadingVersions(prev => ({ ...prev, [taskId]: false }))
    }
  }

  const handleUnitClick = (unitId) => {
    if (!buildingPassportId || !unitId) return
    redirect(
      PRIVATE_PATHS.bpDetails
        .replace(':id', buildingPassportId)
        .replace(':folder?', 'units')
        .replace(':unitId?', unitId)
    )
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon style={{ color: '#52c41a' }} />
      case 'failed':
        return <ErrorIcon style={{ color: '#ff4d4f' }} />
      case 'generating':
      case 'pending':
        return <HourglassEmptyIcon style={{ color: '#faad14' }} />
      default:
        return null
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Ready'
      case 'failed':
        return 'Failed'
      case 'generating':
        return 'Generating...'
      case 'pending':
        return 'Pending'
      default:
        return status
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const allUnits = React.useMemo(() => {
    const names = new Set()
    reports.forEach(r => {
      if (r.task?.units?.length > 0) r.task.units.forEach(u => names.add(u.name))
      else if (r.task?.unit?.name) names.add(r.task.unit.name)
    })
    return [...names].sort()
  }, [reports])

  const filteredReports = React.useMemo(() => {
    if (!unitFilter) return reports
    return reports.filter(r => {
      const units = getUnitList(r.task)
      return units.some(u => u.name === unitFilter)
    })
  }, [reports, unitFilter])

  if (isLoading && reports.length === 0) {
    return (
      <LoadingWrapper>
        <LoadingSpinner />
        <LoadingText>Loading reports...</LoadingText>
      </LoadingWrapper>
    )
  }

  if (error) {
    return (
      <ErrorWrapper>
        <ErrorIcon style={{ fontSize: 40, opacity: 0.5, marginBottom: 12 }} />
        <ErrorText>{error}</ErrorText>
        <RefreshButton onClick={() => fetchReports()}>
          <RefreshIcon /> Try Again
        </RefreshButton>
      </ErrorWrapper>
    )
  }

  if (reports.length === 0) {
    return (
      <EmptyWrapper>
        <EmptyIconWrapper>
          <DescriptionIcon />
        </EmptyIconWrapper>
        <EmptyTitle>No Task Reports Yet</EmptyTitle>
        <EmptyDescription>
          Task reports are automatically generated when tasks are completed.
        </EmptyDescription>
      </EmptyWrapper>
    )
  }

  return (
    <Container>
      <Header>
        <Title>Task Reports</Title>
        <HeaderActions>
          {allUnits.length > 0 && (
            <UnitFilterSelect
              value={unitFilter || null}
              onChange={v => setUnitFilter(v || '')}
              options={allUnits.map(name => ({ value: name, label: name }))}
              clearable
              clearLabel='All units'
              placeholder='All units'
              searchPlaceholder='Search units…'
              ariaLabel='Filter by unit'
            />
          )}
          <RefreshButton onClick={() => fetchReports(page)}>
            <RefreshIcon /> Refresh
          </RefreshButton>
        </HeaderActions>
      </Header>

      <ReportsList>
        {filteredReports.map((report) => {
          const units = getUnitList(report.task)
          const taskId = report.task?.id
          const isExpanded = expandedVersions[taskId]
          const versions = versionData[taskId]
          const isLoadingVersions = loadingVersions[taskId]

          return (
            <React.Fragment key={report.id}>
              <ReportItem>
                <ReportIcon>
                  <DescriptionIcon />
                </ReportIcon>

                <ReportInfo>
                  <ReportTitle>
                    {report.task?.title || 'Task Report'}
                  </ReportTitle>
                  {units.length > 0 && (
                    <UnitChipsRow>
                      {units.map(unit => (
                        <UnitChip
                          key={unit.id}
                          onClick={(e) => { e.stopPropagation(); handleUnitClick(unit.id) }}
                          $clickable
                        >
                          <MeetingRoomOutlinedIcon style={{ fontSize: 13 }} />
                          {unit.name}
                        </UnitChip>
                      ))}
                    </UnitChipsRow>
                  )}
                  <ReportMeta>
                    <span>{report.filename}</span>
                    <MetaDot />
                    <span>{formatFileSize(report.fileSize)}</span>
                    <MetaDot />
                    <span>{moment(report.createdAt).format('DD MMM YYYY, HH:mm')}</span>
                    {report.versionNumber > 1 && (
                      <>
                        <MetaDot />
                        <span>v{report.versionNumber}</span>
                      </>
                    )}
                  </ReportMeta>
                  {report.generatedBy && (
                    <ReportGenerator>
                      Generated by {report.generatedBy.firstName} {report.generatedBy.lastName}
                    </ReportGenerator>
                  )}
                </ReportInfo>

                <ReportStatus $status={report.status}>
                  {getStatusIcon(report.status)}
                  <span>{getStatusLabel(report.status)}</span>
                </ReportStatus>

                {report.status === 'completed' && (
                  <>
                    {report.versionCount > 1 && (
                      <VersionsButton onClick={() => handleToggleVersions(report)}>
                        <HistoryIcon />
                        <VersionsBadge>{report.versionCount}</VersionsBadge>
                        {isExpanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
                      </VersionsButton>
                    )}
                    <DownloadButton
                      onClick={() => handleDownload(report)}
                      disabled={downloadingId === report.id}
                    >
                      {downloadingId === report.id ? (
                        <SmallSpinner />
                      ) : (
                        <DownloadIcon />
                      )}
                      Download
                    </DownloadButton>
                  </>
                )}

                {report.status === 'failed' && report.errorMessage && (
                  <ErrorMessage title={report.errorMessage}>
                    <ErrorIcon style={{ fontSize: 14 }} />
                  </ErrorMessage>
                )}
              </ReportItem>

              {isExpanded && (
                <VersionsPanel>
                  {isLoadingVersions ? (
                    <VersionsLoading>
                      <SmallSpinner $dark />
                      <span>Loading versions...</span>
                    </VersionsLoading>
                  ) : versions ? (
                    versions.map(version => (
                      <VersionRow key={version.id}>
                        <VersionInfo>
                          <VersionLabel>Version {version.versionNumber}</VersionLabel>
                          <VersionMeta>
                            {moment(version.createdAt).format('DD MMM YYYY, HH:mm')}
                            <MetaDot />
                            {formatFileSize(version.fileSize)}
                            {version.generatedBy && (
                              <>
                                <MetaDot />
                                {version.generatedBy.firstName} {version.generatedBy.lastName}
                              </>
                            )}
                          </VersionMeta>
                        </VersionInfo>
                        {version.status === 'completed' && (
                          <VersionDownloadButton
                            onClick={() => handleDownload(version)}
                            disabled={downloadingId === version.id}
                          >
                            {downloadingId === version.id ? (
                              <SmallSpinner />
                            ) : (
                              <DownloadIcon style={{ fontSize: 16 }} />
                            )}
                          </VersionDownloadButton>
                        )}
                        {version.status === 'failed' && (
                          <VersionStatusBadge $status="failed">Failed</VersionStatusBadge>
                        )}
                        {(version.status === 'generating' || version.status === 'pending') && (
                          <VersionStatusBadge $status="pending">{getStatusLabel(version.status)}</VersionStatusBadge>
                        )}
                      </VersionRow>
                    ))
                  ) : null}
                </VersionsPanel>
              )}
            </React.Fragment>
          )
        })}
      </ReportsList>

      {meta && meta.lastPage > 1 && (
        <Pagination>
          <PageButton
            onClick={() => fetchReports(page - 1)}
            disabled={page === 1 || isLoading}
          >
            Previous
          </PageButton>
          <PageInfo>
            Page {page} of {meta.lastPage}
          </PageInfo>
          <PageButton
            onClick={() => fetchReports(page + 1)}
            disabled={page === meta.lastPage || isLoading}
          >
            Next
          </PageButton>
        </Pagination>
      )}
    </Container>
  )
}

export default TaskReports

// Animations
const spin = keyframes`
  to { transform: rotate(360deg); }
`

// Styled Components
const Container = styled.div`
  padding: 20px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const UnitFilterSelect = styled(SearchableSelect)`
  width: 180px;
`

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const ReportsList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  overflow: hidden;
`

const ReportItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.015);
  }
`

const ReportIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #e6f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
    color: #1890ff;
  }
`

const ReportInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const ReportTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ReportMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  flex-wrap: wrap;
`

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const ReportGenerator = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  margin-top: 4px;
`

const UnitChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
`

const UnitChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #33becc;
  background: #e6f7f9;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #e6f7f9;
    border-color: #93c5fd;
    color: #33becc;
  }
`

const ReportStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $status }) =>
    $status === 'completed' ? '#52c41a' :
    $status === 'failed' ? '#ff4d4f' :
    '#faad14'};
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }
`

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.secondary || '#33becc'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(51, 190, 204, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const VersionsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey || '#ddd'};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  font-size: 13px;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const VersionsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #1890ff;
  color: white;
  font-size: 11px;
  font-weight: 600;
`

const VersionsPanel = styled.div`
  background: ${({ theme }) => theme.colors.lightGrey || '#f9fafb'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  padding: 8px 20px 8px 80px;
`

const VersionsLoading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const VersionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);

  &:last-child {
    border-bottom: none;
  }
`

const VersionInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const VersionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const VersionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const VersionDownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey || '#ddd'};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.secondary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const VersionStatusBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  color: ${({ $status }) => $status === 'failed' ? '#ff4d4f' : '#faad14'};
  background: ${({ $status }) => $status === 'failed' ? '#fff2f0' : '#fffbe6'};
  flex-shrink: 0;
`

const SmallSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ $dark }) => $dark ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.3)'};
  border-top-color: ${({ $dark }) => $dark ? '#666' : 'white'};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const ErrorMessage = styled.div`
  color: #ff4d4f;
  cursor: help;

  svg {
    font-size: 18px;
  }
`

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`

const PageButton = styled.button`
  padding: 8px 16px;
  background: ${({ disabled }) => disabled ? '#f5f5f5' : 'white'};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ disabled, theme }) => disabled ? '#ccc' : theme.colors.haiti};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const PageInfo = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

// Loading, Empty, Error states
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${({ theme }) => theme.colors.mediumGrey};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const LoadingText = styled.p`
  margin-top: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`

const EmptyIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    font-size: 32px;
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const EmptyTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
`

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0;
  max-width: 300px;
`

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const ErrorText = styled.p`
  font-size: 14px;
  color: #ff4d4f;
  margin: 0 0 16px 0;
`
